import "assets/css/privacy.css";

const Privacy = () => {
  return (
    <article className="privacy-page">
      <main className="privacy-page__main">
        <section className="privacy-page__block privacy-page__head">
          <h1 className="privacy-page__block-title">Privacy Policy</h1>
        </section>

        <section className="privacy-page__block">
          <p className="privacy-page__block-descr">
            <strong>Last Revised on December 29, 2024</strong>
          </p>

          <p className="privacy-page__block-descr">
            Welcome to the Privacy Policy (this “Privacy Policy”) for the
            website
            <a
              href="https://www.twotwentyk.io"
              className="privacy-page__block-link"
            >
              www.twotwentyk.io
            </a>{" "}
            (the “Website”), the derivative gaming platform (the “Platform”),
            and all other applications, content, smart contracts, tools,
            features, and functionality offered on or through the Website and
            the Platform (collectively with the Website and the Platform, the
            “Services”), in each case operated by or on behalf of 10XCHI, Inc.
            (“10XCHI” “we,” “us,” or “our”).
          </p>

          <p className="privacy-page__block-descr">
            We are committed to respecting and protecting your privacy and
            complying with any applicable law and regulation regarding your
            personal information. This Privacy Policy describes how we collect,
            use, disclose, and protect your personal information, including
            across the Website and Platform and in connection with the Services.
          </p>

          <p className="privacy-page__block-descr">
            Where appropriate or legally required, we will describe how personal
            information about you that we collect through our Services will be
            used and will provide choices about whether or not to allow us to
            engage in that use. To exercise your choice, you can contact us to
            correct, update, amend or request deletion of your personal
            information at
            <a
              href="mailto:admin@twotwentyk.io"
              className="privacy-page__block-link"
            >
              admin@twotwentyk.io
            </a>
            , or at the following address: 10XCHI, Inc., 120 Palmetto Park Road,
            Suite 400, Boca Raton, FL. 33431.
          </p>

          <p className="privacy-page__block-descr">
            Please read this Privacy Policy carefully. If you do not agree with
            this Privacy Policy in general or any part of it, you should not
            access the website, use our Services, or otherwise provide your
            information to us.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Information We Collect</h2>
          <p className="privacy-page__block-descr">
            Information we collect falls into one of two categories:
            “automatically collected” information and “voluntarily provided”
            information.
          </p>

          <p className="privacy-page__block-descr">
            “Automatically collected” information refers to any information
            automatically sent by your devices in the course of accessing our
            Services.
          </p>

          <p className="privacy-page__block-descr">
            “Voluntarily provided” information refers to any information you
            knowingly and actively provide us when using or participating in any
            of our Services.
          </p>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">
            Automatically Collected Information
          </h3>

          <h4 className="privacy-page__block-title">Log Data</h4>
          <p className="privacy-page__block-descr">
            When you visit our Services, our servers may automatically log the
            standard data provided by your web browser. It may include your
            device’s Internet Protocol (IP) address, geolocation data, your
            browser type and version, the pages you visit, the time and date of
            your visit, the time spent on each page, the referring site, website
            elements clicked, and other details about your visit.
          </p>

          <p className="privacy-page__block-descr">
            Additionally, if you encounter certain errors while using the site,
            we may automatically collect data about the error and the
            circumstances surrounding its occurrence. This data may include
            technical details about your device, what you were trying to do when
            the error happened, and other technical information relating to the
            problem. You may or may not receive notice of such errors, even in
            the moment they occur, that they have occurred, or what the nature
            of the error is.
          </p>

          <p className="privacy-page__block-descr">
            Please be aware that while this information may not be personally
            identifying by itself, it may be possible to combine it with other
            data to personally identify individual persons.
          </p>
        </section>

        <section className="privacy-page__block">
          <h4 className="privacy-page__block-title">Device Data</h4>
          <p className="privacy-page__block-descr">
            When you visit the Platform or interact with our Services, we may
            automatically collect data about your device, such as:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Technical information about your device, such as device type,
              browser type, IP address, operating system, and device identifier.
              We collect this information automatically from your device and web
              browser through cookies and similar technologies.
            </li>
            <li className="privacy-page__block-list-item">
              Information about how you interact with us. We may collect
              technical data about your usage of the Platforms and Apps and how
              you interact with our digital advertisements and promotions, such
              as content viewed or downloaded, features used, links clicked, our
              promotional emails opened, and dates and times of interactions. We
              collect this information using cookies and similar technologies.
            </li>
            <li className="privacy-page__block-list-item">
              We may collect location information, including precise real-time
              location information from your device and imprecise location
              information derived from, for example, your IP address or postal
              code.
            </li>
          </ul>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">
            Voluntarily Provided Information
          </h3>

          <h4 className="privacy-page__block-title">Personal Information</h4>
          <p className="privacy-page__block-descr">
            We may ask for personal information—for example, when you register
            an account, make a purchase, request support for our Services, or
            when you otherwise contact us—which may include one or more of the
            following:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">Name;</li>
            <li className="privacy-page__block-list-item">Email address;</li>
            <li className="privacy-page__block-list-item">Phone number;</li>
            <li className="privacy-page__block-list-item">
              Other information necessary for Anti-Money Laundering (“AML”),
              Counter Financing of Terrorism (“CFT”), and Know Your Customer
              (“KYC”) compliance purposes;
            </li>
            <li className="privacy-page__block-list-item">
              Any information provided by you when you register for an Account
              to use the Platform;
            </li>
            <li className="privacy-page__block-list-item">
              Transaction data, including comments, questions, requests and
              orders you may make, as well as purchase order history and
              information;
            </li>
            <li className="privacy-page__block-list-item">
              Financial information needed to process payments if you make
              purchases, such as credit card or other financial account
              information; and
            </li>
            <li className="privacy-page__block-list-item">
              Any other information you choose to provide, such as when you fill
              in a form, file a support ticket, provide screenshots, conduct a
              search, update or add information to your account, respond to
              surveys, participate in promotions, correspond with us, or use
              other features of our Services.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            You have choices about the personal information you provide us. You
            may choose not to provide information that we request, but if you do
            so, we may not be able to provide you a relevant service or a
            particular feature for our Services.
          </p>
        </section>

        <section className="privacy-page__block">
          <h4 className="privacy-page__block-title">Third-Party Information</h4>
          <p className="privacy-page__block-descr">
            We may collect personal information through our business partners.
            This information may include contact information, such as name,
            e-mail address, and phone number.
          </p>

          <p className="privacy-page__block-descr">
            We may also obtain personal information from other third-party
            sources, including publicly and commercially available sources. We
            may combine the information we receive from our business partners
            and other third-party sources with information that we collect from
            you or your device, as described above.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Collection and Use of Information
          </h2>
          <p className="privacy-page__block-descr">
            We may collect personal information from you when you do any of the
            following on our Services:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Register for an Account
            </li>
            <li className="privacy-page__block-list-item">
              Use a mobile device, personal computer, or web browser to access
              our Services
            </li>
            <li className="privacy-page__block-list-item">
              Contact us via email, social media, chat programs, or on any
              similar technologies
            </li>
            <li className="privacy-page__block-list-item">
              When you mention us on social media
            </li>
          </ul>

          <p className="privacy-page__block-descr">
            We may use information for the following purposes, and personal
            information will not be further processed in a manner that is
            incompatible with these purposes:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              To provide you with our Services or carrying out transactions that
              you or our business partners have requested;
            </li>
            <li className="privacy-page__block-list-item">
              To enable you to customize or personalize your experience of our
              Services;
            </li>
            <li className="privacy-page__block-list-item">
              To operate, improve, and develop our Services;
            </li>
            <li className="privacy-page__block-list-item">
              To contact and communicate with you;
            </li>
            <li className="privacy-page__block-list-item">
              To enable you to access and use our Services, associated
              interactive features, associated applications, and associated
              social media or chat platforms;
            </li>
            <li className="privacy-page__block-list-item">
              To provide you with information about our products, services, and
              transactions, and advertisements that may be of interest to you;
            </li>
            <li className="privacy-page__block-list-item">
              To provide you and our business partners a more consistent
              experience in interacting with us, including by learning more
              about you and how you use and interact with our Services and
              planning, managing, and performing under our contractual
              relationships with our business partners;
            </li>
            <li className="privacy-page__block-list-item">
              To comply with our legal obligations and resolve any disputes that
              we may have;
            </li>
            <li className="privacy-page__block-list-item">
              For security and fraud prevention, and to ensure that our sites
              and apps are safe, secure, and used in accordance with our Terms
              of Use;
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            We may anonymize or aggregate any of the information we collect and
            use it for any purpose, including for research and
            product-development purposes. Such information will not identify you
            individually.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Security of Your Personal Information
          </h2>
          <p className="privacy-page__block-descr">
            When we collect and process personal information, and while we
            retain this information, we will protect it within commercially
            acceptable means to prevent loss and theft, as well as unauthorized
            access, disclosure, copying, use, or modification.
          </p>

          <p className="privacy-page__block-descr">
            Your personal information will generally be stored in our databases
            or databases maintained by our service providers. Many of these
            databases are stored on servers located in the European Union and
            the United States. We take appropriate measures, by contract or
            otherwise, to provide adequate protection for personal information
            that is disclosed to a third party or transferred to another
            country.
          </p>

          <p className="privacy-page__block-descr">
            Although we will implement and maintain practices to protect the
            personal information you provide to us, we advise that no method of
            electronic transmission or storage is 100% secure, and no one can
            guarantee absolute data security. We recommend that you do not use
            unsecure channels to communicate sensitive or confidential
            information to us. Any information you transmit to us is at your own
            risk.
          </p>

          <p className="privacy-page__block-descr">
            You are responsible for selecting any password, to the extent one is
            required, and its overall security strength, ensuring the security
            of your own information within the bounds of our services. For
            example, you are responsible for ensuring any passwords associated
            with accessing your personal information and accounts are secure and
            confidential.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            How Long We Keep Your Personal Information
          </h2>
          <p className="privacy-page__block-descr">
            How long we keep your personal information will vary and will depend
            on the purpose and use of information collected. There are legal
            requirements that we keep some types of data for specific periods.
            Otherwise, we will retain it for no longer than is necessary for the
            purposes for which the data was collected.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Children’s Privacy</h2>
          <p className="privacy-page__block-descr">
            We do not aim any of our Services directly at children under the age
            of 18, and we do not knowingly collect personal information about
            children under 18. If we become aware that we have unknowingly
            collected information about a child under 18 years of age, we will
            make commercially reasonable efforts to delete such information from
            our database.
          </p>

          <p className="privacy-page__block-descr">
            If you are the parent or guardian of a child under 18 years of age
            who has provided us with their personal information, you may contact
            us using the below information to request that it be deleted.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Disclosure of Personal Information to Third Parties
          </h2>
          <p className="privacy-page__block-descr">
            We may disclose personal information to third parties in the
            following circumstances:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              To our parent, subsidiary, and/or affiliated entities, and their
              respective employees, contractors, and/or related entities, as
              required to provide the Services;
            </li>
            <li className="privacy-page__block-list-item">
              With service providers for the purpose of enabling them to provide
              our Services, including IT service providers, data storage,
              hosting and server providers, analytics, error loggers, debt
              collectors, maintenance or problem-solving providers, professional
              advisors, and payment systems operators;
            </li>
            <li className="privacy-page__block-list-item">
              Advertising partners;
            </li>
            <li className="privacy-page__block-list-item">
              Credit reporting agencies, courts, tribunals, and regulatory
              authorities, in the event you fail to pay for goods or services we
              have provided to you;
            </li>
            <li className="privacy-page__block-list-item">
              Courts, tribunals, regulatory authorities, and law enforcement
              officers, as required by law, or any obligations arising
              thereunder, or in connection with any actual or prospective legal
              proceedings, or in order to establish, exercise, or defend our
              legal rights or those of a third party;
            </li>
            <li className="privacy-page__block-list-item">
              To prevent physical harm, financial loss, or fraud on our
              Services;
            </li>
            <li className="privacy-page__block-list-item">
              An entity that buys, or to which we transfer, all or substantially
              all of our assets and business;
            </li>
            <li className="privacy-page__block-list-item">
              With third parties in connection with our sale, purchase, merger,
              reorganization, liquidation or dissolution or under similar
              circumstances. If such an event occurs, we will take appropriate
              steps to protect your personal information;
            </li>
            <li className="privacy-page__block-list-item">
              With your permission or at your request.
            </li>
          </ul>

          <p className="privacy-page__block-descr">
            We may share anonymized or aggregated information internally and
            with third parties for any purpose, including, but not limited to,
            selling such information to third parties. Such information will not
            identify you individually.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Your Rights and Controlling Your Personal Information
          </h2>
          <p className="privacy-page__block-descr">
            <strong>Correcting or Updating Your Personal Information:</strong>
            You may review, correct, and update personal information you provide
            to us by contacting us through email or by writing to us at the
            address set forth below.
          </p>

          <p className="privacy-page__block-descr">
            <strong>Opting Out:</strong> If you have previously agreed to us
            using your personal information for direct marketing purposes, you
            may change your mind at any time by contacting us through email or
            by writing to us at the address set forth below.
          </p>

          <p className="privacy-page__block-descr">
            <strong>Complaints:</strong> If you believe that we have breached a
            relevant data protection law and wish to make a complaint, you may
            contact us through email or by writing to us at the address set
            forth below.
          </p>

          <p className="privacy-page__block-descr">
            <strong>Contact Information:</strong>
          </p>
          <p className="privacy-page__block-descr">
            10XCHI, Inc.
            <br />
            120 Palmetto Park Road, Suite 400
            <br />
            Boca Raton, FL. 33431
          </p>
        </section>
        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Cookies</h2>
          <p className="privacy-page__block-descr">
            We use cookies and similar tracking technologies to track the
            activity on our Service and hold certain information. Cookies are
            files with a small amount of data which may include an anonymous
            unique identifier. Cookies are sent to your browser from a website
            and stored on your device. Tracking technologies also used are
            beacons, tags, and scripts to collect and track information and to
            improve and analyze our Service.
          </p>
          <p className="privacy-page__block-descr">
            You can instruct your browser to refuse all cookies or to indicate
            when a cookie is being sent. However, if you do not accept cookies,
            you may not be able to use some portions of our Service.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Use of Data</h2>
          <p className="privacy-page__block-descr">
            10XCHI, Inc. uses the collected data for various purposes:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">To provide and maintain our Service</li>
            <li className="privacy-page__block-list-item">To notify you about changes to our Service</li>
            <li className="privacy-page__block-list-item">
              To allow you to participate in interactive features of our Service
              when you choose to do so
            </li>
            <li className="privacy-page__block-list-item">To provide customer support</li>
            <li className="privacy-page__block-list-item">
              To gather analysis or valuable information so that we can improve
              our Service
            </li>
            <li className="privacy-page__block-list-item">To monitor the usage of our Service</li>
            <li className="privacy-page__block-list-item">To detect, prevent and address technical issues</li>
          </ul>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Transfer of Data</h2>
          <p className="privacy-page__block-descr">
            Your information, including Personal Data, may be transferred to —
            and maintained on — computers located outside of your state,
            province, country or other governmental jurisdiction where the data
            protection laws may differ from those of your jurisdiction.
          </p>
          <p className="privacy-page__block-descr">
            If you are located outside United States and choose to provide
            information to us, please note that we transfer the data, including
            Personal Data, to United States and process it there.
          </p>
          <p className="privacy-page__block-descr">
            Your consent to this Privacy Policy followed by your submission of
            such information represents your agreement to that transfer.
          </p>
          <p className="privacy-page__block-descr">
            10XCHI, Inc. will take all steps reasonably necessary to ensure that
            your data is treated securely and in accordance with this Privacy
            Policy and no transfer of your Personal Data will take place to an
            organization or a country unless there are adequate controls in
            place including the security of your data and other personal
            information.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Disclosure of Data</h2>
          <p className="privacy-page__block-descr">
            10XCHI, Inc. may disclose your Personal Data in the good faith
            belief that such action is necessary to:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">To comply with a legal obligation</li>
            <li className="privacy-page__block-list-item">
              To protect and defend the rights or property of 10XCHI, Inc.
            </li>
            <li className="privacy-page__block-list-item">
              To prevent or investigate possible wrongdoing in connection with
              the Service
            </li>
            <li className="privacy-page__block-list-item">
              To protect the personal safety of users of the Service or the
              public
            </li>
            <li className="privacy-page__block-list-item">To protect against legal liability</li>
          </ul>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Security of Data</h2>
          <p className="privacy-page__block-descr">
            The security of your data is important to us but remember that no
            method of transmission over the Internet or method of electronic
            storage is 100% secure. While we strive to use commercially
            acceptable means to protect your Personal Data, we cannot guarantee
            its absolute security.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Service Providers</h2>
          <p className="privacy-page__block-descr">
            We may employ third party companies and individuals to facilitate
            our Service ("Service Providers"), to provide the Service on our
            behalf, to perform Service-related services or to assist us in
            analyzing how our Service is used.
          </p>
          <p className="privacy-page__block-descr">
            These third parties have access to your Personal Data only to
            perform these tasks on our behalf and are obligated not to disclose
            or use it for any other purpose.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Links to Other Sites</h2>
          <p className="privacy-page__block-descr">
            Our Service may contain links to other sites that are not operated
            by us. If you click on a third party link, you will be directed to
            that third party's site. We strongly advise you to review the
            Privacy Policy of every site you visit.
          </p>
          <p className="privacy-page__block-descr">
            We have no control over and assume no responsibility for the
            content, privacy policies or practices of any third party sites or
            services.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Children's Privacy</h2>
          <p className="privacy-page__block-descr">
            Our Service does not address anyone under the age of 18
            ("Children").
          </p>
          <p className="privacy-page__block-descr">
            We do not knowingly collect personally identifiable information from
            anyone under the age of 18. If you are a parent or guardian and you
            are aware that your Child has provided us with Personal Data, please
            contact us. If we become aware that we have collected Personal Data
            from children without verification of parental consent, we take
            steps to remove that information from our servers.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Changes to This Privacy Policy
          </h2>
          <p className="privacy-page__block-descr">
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page.
          </p>
          <p className="privacy-page__block-descr">
            We will let you know via email and/or a prominent notice on our
            Service, prior to the change becoming effective and update the
            "effective date" at the top of this Privacy Policy.
          </p>
          <p className="privacy-page__block-descr">
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Contact Us</h2>
          <p className="privacy-page__block-descr">
            If you have any questions about this Privacy Policy, please contact
            us:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              By email:
              <a
                href="mailto:admin@twotwentyk.io"
                className="privacy-page__block-link"
              >
                admin@twotwentyk.io
              </a>
            </li>
            <li className="privacy-page__block-list-item">
              By visiting this page on our website:
              <a
                href="https://www.twotwentyk.io/contact"
                className="privacy-page__block-link"
              >
                https://www.twotwentyk.io/contact
              </a>
            </li>
            <li className="privacy-page__block-list-item">
              By mail: 10XCHI, Inc., 120 Palmetto Park Road, Suite 400, Boca
              Raton, FL. 33431
            </li>
          </ul>
        </section>
      </main>
    </article>
  );
};

export default Privacy;
